import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Checkbox, Input, Radio, Typography } from 'antd';
import ComponentHeader from 'app/shared/ui/ComponentHeader';
import ErrorSummary from 'app/pages/DiscountEditor/components/ErrorSummary';
import ScrollPanel from 'app/shared/ui/ScrollPanel';
import { Action } from 'app/pages/DiscountEditor/actionTypes';
import {
  getCountrySelectionMode,
  getSelectedCountries,
  getCountries,
  CountrySelection,
} from 'app/pages/DiscountEditor/modules/CountrySelection';
import {
  changeCountrySelection,
  changeCountrySelectionMode,
  addCountrySelection,
} from 'app/pages/DiscountEditor/actions';
import { getSectionError } from 'app/pages/DiscountEditor/modules/Editor';
import { SaveDiscountErrorCategory } from 'app/shared/model/SaveDiscountResultDto';
import { Country } from 'app/shared/model';
import { CountrySelectionMode } from 'app/shared/model/CountrySelectionMode';
import { getSelectedDivisionIds } from 'app/pages/DiscountEditor/modules/Divisions/selectors';
import { getRegions, RegionDto } from 'app/shared/api/getRegions';
import { getCountriesWithRegions, CountryWithRegionDto } from 'app/shared/api/getCountriesWithRegions';
import './CountrySelectionEditor.css';

const { Text } = Typography;

interface StateProps {
  errorMessage?: string;
  countrySelectionMode: CountrySelectionMode;
  countries: Country[];
  selectedCountries: CountrySelection[];
  selectedDivisionIds: number[];
}

interface DispatchProps {
  changeCountrySelectionMode: (mode: CountrySelectionMode) => void;
  changeCountrySelection: (countryId: string, selected: boolean) => void;
  addCountry: (countryId: string) => void;
}

type InternalProps = StateProps & DispatchProps;

type RegionNode = {
  region: RegionDto;
  countries: Array<Country & { regionId: number }>;
};

function normalizeId(v: string | number): string {
  return String(v);
}

type CountryLookup = {
  idToName: Record<string, string>;
  numericIdToStringId: Record<string, string>;
  nameToStringId: Record<string, string>;
};

function buildCountryLookup(countries: Country[]): CountryLookup {
  const idToName: Record<string, string> = {};
  const numericIdToStringId: Record<string, string> = {};
  const nameToStringId: Record<string, string> = {};

  for (const c of countries as any[]) {
    const stringId = normalizeId(c.id);
    idToName[stringId] = c.name ?? '';
    const nameKey = (c.name ?? '').trim().toLowerCase();
    if (nameKey && !nameToStringId[nameKey]) {
      nameToStringId[nameKey] = stringId;
    }

    const numericId = (c as any).numericId;
    if (numericId !== undefined && numericId !== null && !isNaN(Number(numericId))) {
      numericIdToStringId[normalizeId(numericId)] = stringId;
    }
  }

  return { idToName, numericIdToStringId, nameToStringId };
}

function buildSelectedMap(selectedCountries: CountrySelection[]): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  for (const c of selectedCountries) map[normalizeId(c.id)] = !!c.selected;
  return map;
}

function buildPresenceMap(selectedCountries: CountrySelection[]): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  for (const c of selectedCountries) map[normalizeId(c.id)] = true;
  return map;
}

function getRegionCheckState(regionCountries: Array<{ id: string }>, selectedMap: Record<string, boolean>) {
  const total = regionCountries.length;
  if (total === 0) return { checked: false, indeterminate: false };
  let selectedCount = 0;
  for (const c of regionCountries) if (selectedMap[normalizeId(c.id)]) selectedCount++;
  if (selectedCount === 0) return { checked: false, indeterminate: false };
  if (selectedCount === total) return { checked: true, indeterminate: false };
  return { checked: false, indeterminate: true };
}

const CountrySelectionEditor: React.FC<InternalProps> = props => (
  <CountrySelectionEditorInternal {...props} />
);

const CountrySelectionEditorInternal: React.FC<InternalProps> = props => {
  const divisionId = props.selectedDivisionIds?.length ? Number(props.selectedDivisionIds[0]) : 0;
  const [search, setSearch] = React.useState('');
  const [expanded, setExpanded] = React.useState<Record<number, boolean>>({});
  const [regions, setRegions] = React.useState<RegionDto[]>([]);
  const [countriesWithRegions, setCountriesWithRegions] = React.useState<CountryWithRegionDto[]>([]);
  const [loading, setLoading] = React.useState(false);

  const selectedMap = React.useMemo(() => buildSelectedMap(props.selectedCountries), [props.selectedCountries]);
  const presenceMap = React.useMemo(() => buildPresenceMap(props.selectedCountries), [props.selectedCountries]);
  const countryLookup = React.useMemo(() => buildCountryLookup(props.countries), [props.countries]);

  React.useEffect(() => {
    const needsPanel = props.countrySelectionMode !== CountrySelectionMode.AllCountries;
    if (!needsPanel) return;
    if (!divisionId) {
      setRegions([]);
      setCountriesWithRegions([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    Promise.all([getRegions(divisionId), getCountriesWithRegions(divisionId)])
      .then(([r, cwr]) => {
        if (cancelled) return;
        setRegions(r || []);
        setCountriesWithRegions(cwr || []);
      })
      .catch(() => {
        if (cancelled) return;
        setRegions([]);
        setCountriesWithRegions([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [divisionId, props.countrySelectionMode]);

  const regionNodes: RegionNode[] = React.useMemo(() => {
    const byRegionId: Record<number, Array<Country & { regionId: number }>> = {};
    for (const dto of countriesWithRegions) {
      const regionId = dto.regionId;
      if (regionId == null || regionId === 0) continue;
      const numericKey = normalizeId(dto.id);
      const nameKey = (dto.name ?? '').trim().toLowerCase();
      const mappedStringId =
        countryLookup.numericIdToStringId[numericKey] ??
        (nameKey ? countryLookup.nameToStringId[nameKey] : undefined) ??
        (countryLookup.idToName[numericKey] ? numericKey : undefined);

      if (!mappedStringId) continue;
      const name = countryLookup.idToName[mappedStringId] ?? dto.name ?? '';
      if (!name) continue;
      if (!byRegionId[regionId]) byRegionId[regionId] = [];
      byRegionId[regionId].push({ id: mappedStringId, name, regionId });
    }
    for (const k of Object.keys(byRegionId)) {
      byRegionId[Number(k)].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    const nodes: RegionNode[] = (regions || []).map((r) => ({
      region: r,
      countries: byRegionId[r.id] ?? [],
    }));

    const assigned = new Set<string>();
    for (const n of nodes) for (const c of n.countries) assigned.add(normalizeId(c.id));
    const unassignedCountries: Array<Country & { regionId: number }> = (props.countries || [])
      .filter((c: any) => !!countryLookup.idToName[normalizeId(c.id)] && !assigned.has(normalizeId(c.id)))
      .map((c: any) => ({ id: normalizeId(c.id), name: c.name ?? '', regionId: -1 }))
      .filter(c => !!c.name)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    const out = nodes
      .filter(n => n.region?.name)
      .sort((a, b) => (a.region.name || '').localeCompare(b.region.name || ''));

    if (unassignedCountries.length > 0) {
      out.push({
        region: { id: -1, name: 'Other countries' },
        countries: unassignedCountries,
      });
    }

    return out;
  }, [regions, countriesWithRegions, countryLookup]);

  const normalizedSearch = search.trim().toLowerCase();
  const filteredNodes = React.useMemo(() => {
    if (!normalizedSearch) return regionNodes;

    const matches = (s: string | undefined) => (s ?? '').toLowerCase().includes(normalizedSearch);
    const out: RegionNode[] = [];
    for (const n of regionNodes) {
      const regionMatch = matches(n.region.name);
      if (regionMatch) {
        out.push(n);
        continue;
      }
      const filteredCountries = n.countries.filter(c => matches(c.name));
      if (filteredCountries.length > 0) {
        out.push({ ...n, countries: filteredCountries });
      }
    }
    return out;
  }, [regionNodes, normalizedSearch]);

  const handleToggleRegionExpand = (regionId: number) => {
    setExpanded(prev => ({ ...prev, [regionId]: !prev[regionId] }));
  };

  const setCountrySelected = (countryId: string, selected: boolean) => {
    const id = normalizeId(countryId);
    if (selected) {
      if (presenceMap[id]) props.changeCountrySelection(id, true);
      else props.addCountry(id);
      return;
    }
    if (presenceMap[id]) props.changeCountrySelection(id, false);
  };

  const setManyCountries = (countryIds: string[], selected: boolean) => {
    for (const id of countryIds) setCountrySelected(id, selected);
  };

  const onRegionCheckChange = (regionId: number, checked: boolean) => {
    const node = regionNodes.find(n => n.region.id === regionId);
    const countryIds = (node?.countries ?? []).map(c => c.id);
    setManyCountries(countryIds, checked);
    setExpanded(prev => ({ ...prev, [regionId]: true }));
  };

  const onCountryCheckChange = (countryId: string, checked: boolean) => {
    setCountrySelected(countryId, checked);
  };

  const modeValue =
    props.countrySelectionMode === CountrySelectionMode.AllCountries
      ? 'all'
      : props.countrySelectionMode === CountrySelectionMode.IncludeSpecific
        ? 'include'
        : 'exclude';

  const onModeChange = (value: string) => {
    const mode =
      value === 'all'
        ? CountrySelectionMode.AllCountries
        : value === 'include'
          ? CountrySelectionMode.IncludeSpecific
          : CountrySelectionMode.ExcludeSpecific;
    props.changeCountrySelectionMode(mode);
  };

  return (
    <div className="country-selection-editor">
      <ComponentHeader text="Countries" />
      <div className="country-selection-editor__content">
        <ErrorSummary errorMessage={props.errorMessage} />

        <div className="country-selection-editor__mode">
          <Text>The discount applies to</Text>
          <div className="country-selection-editor__mode-options">
            <Radio.Group
              value={modeValue}
              onChange={(e) => onModeChange(e.target.value)}
            >
              <Radio value="all">all regions and countries</Radio>
              <Radio value="include">including below</Radio>
              <Radio value="exclude">excluding below</Radio>
            </Radio.Group>
          </div>
        </div>

        {props.countrySelectionMode !== CountrySelectionMode.AllCountries && (
          <>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find region or country"
              className="country-selection-editor__search"
              allowClear
            />

            <ScrollPanel height="220px" className="region-country-tree">
              {loading && (
                <div className="country-selection-editor__tree-message">
                  <Text type="secondary">Loading regions…</Text>
                </div>
              )}

              {!loading && filteredNodes.length === 0 && (
                <div className="country-selection-editor__tree-message">
                  <Text type="secondary">No regions or countries found</Text>
                </div>
              )}

              {filteredNodes.map((n) => {
                const { checked, indeterminate } = getRegionCheckState(n.countries, selectedMap);
                const isExpanded = !!expanded[n.region.id] || !!normalizedSearch;
                const title = `${n.region.name} (${n.countries.length})`;

                return (
                  <div key={n.region.id} className="country-selection-editor__tree-region">
                    <div className="country-selection-editor__tree-region-header">
                      <Checkbox
                        checked={checked}
                        indeterminate={indeterminate}
                        onChange={(e) => onRegionCheckChange(n.region.id, e.target.checked)}
                      />
                      <span
                        className="country-selection-editor__tree-region-title"
                        onClick={() => handleToggleRegionExpand(n.region.id)}
                      >
                        {title}
                      </span>
                    </div>

                    {isExpanded && n.countries.length > 0 && (
                      <div className="country-selection-editor__tree-country-list">
                        {n.countries.map((c) => (
                          <div key={c.id} className="country-selection-editor__tree-country-item">
                            <Checkbox
                              checked={!!selectedMap[normalizeId(c.id)]}
                              onChange={(e) => onCountryCheckChange(c.id, e.target.checked)}
                            >
                              {c.name}
                            </Checkbox>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </ScrollPanel>
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: any): StateProps => ({
  errorMessage: getSectionError(state, SaveDiscountErrorCategory.Countries),
  countrySelectionMode: getCountrySelectionMode(state),
  countries: getCountries(state),
  selectedCountries: getSelectedCountries(state),
  selectedDivisionIds: getSelectedDivisionIds(state),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => ({
  changeCountrySelectionMode: mode => dispatch(changeCountrySelectionMode(mode)),
  changeCountrySelection: (countryId, selected) => dispatch(changeCountrySelection(countryId, selected)),
  addCountry: countryId => dispatch(addCountrySelection(countryId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CountrySelectionEditor);
